import React, { useState, useContext } from "react";
import {
  Col,
  Form,
  Row,
  Button,
  Card,
  InputGroup,
} from "react-bootstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faSearch,
  faTimes,
  faFilter,
  faUser,
  faEnvelope,
  faPhone,
  faIdCard,
  faChalkboardTeacher,
  faGraduationCap,
  faUserCheck,
} from "@fortawesome/free-solid-svg-icons";
import { ClassroomList } from "../../../Context/ClassroomListContext";

interface FilterProps {
  filterStudents: (
    query?: {
      scholarship: string;
      phone: string;
      classroom: string;
      email: string;
      isActive: string;
      name: string;
      surname: string;
      dni: string;
    }
  ) => void;
  clearSearch?: () => void;
  loadingFilter?: boolean;
}

export default function StudentsFilter({
  filterStudents,
  clearSearch,
  loadingFilter,
}: FilterProps) {
  const classroomList = useContext(ClassroomList);
  const [filterSettings, setFilterSettings] = useState({
    scholarship: "",
    phone: "",
    classroom: "",
    email: "",
    isActive: "",
    name: "",
    surname: "",
    dni: "",
  });

  const cleanSearch = () => {
    const emptyFilters = {
      scholarship: "",
      phone: "",
      classroom: "",
      email: "",
      isActive: "",
      name: "",
      surname: "",
      dni: "",
    };
    setFilterSettings(emptyFilters);

    if (clearSearch) {
      clearSearch();
    } else {
      filterStudents();
    }
  };

  const handleSubmit = (event: React.FormEvent) => {
    event.preventDefault();
    filterStudents(filterSettings);
  };

  const handleChange = (
    event: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>
  ) => {
    let { name, value } = event.target;
    if (name === "phone" && value) {
      value = value.replace("-", "").replace(" ", "");
    }
    setFilterSettings((previousState) => ({
      ...previousState,
      [name]: value,
    }));
  };

  const hasActiveFilters = Object.values(filterSettings).some(
    (val) => val.trim() !== ""
  );

  return (
    <Card className="border-0 shadow-sm rounded-lg mb-4 bg-white">
      <Card.Body className="p-4">
        <div className="d-flex align-items-center justify-content-between mb-3 pb-2 border-bottom">
          <div className="d-flex align-items-center">
            <div className="bg-primary text-white rounded-circle p-2 d-flex align-items-center justify-content-center mr-3" style={{ width: "36px", height: "36px" }}>
              <FontAwesomeIcon icon={faFilter} size="sm" />
            </div>
            <div>
              <h6 className="mb-0 font-weight-bold text-dark">Filtros de Búsqueda</h6>
              <small className="text-muted">Encuentra estudiantes por datos personales o estado académico</small>
            </div>
          </div>
          {hasActiveFilters && (
            <Button
              size="sm"
              variant="outline-secondary"
              onClick={cleanSearch}
              disabled={loadingFilter}
              className="rounded-pill px-3"
            >
              <FontAwesomeIcon icon={faTimes} className="mr-1" /> Limpiar
            </Button>
          )}
        </div>

        <Form onSubmit={handleSubmit}>
          <Row className="g-3">
            <Col lg={3} md={6} className="mb-3">
              <Form.Group className="mb-0">
                <Form.Label className="small font-weight-bold text-secondary mb-1">
                  Nombre
                </Form.Label>
                <InputGroup size="sm">
                  <InputGroup.Prepend>
                    <InputGroup.Text className="bg-light border-right-0 text-muted">
                      <FontAwesomeIcon icon={faUser} />
                    </InputGroup.Text>
                  </InputGroup.Prepend>
                  <Form.Control
                    name="name"
                    placeholder="Ej. Juan"
                    value={filterSettings.name}
                    onChange={handleChange}
                    disabled={loadingFilter}
                    className="border-left-0 bg-light"
                  />
                </InputGroup>
              </Form.Group>
            </Col>

            <Col lg={3} md={6} className="mb-3">
              <Form.Group className="mb-0">
                <Form.Label className="small font-weight-bold text-secondary mb-1">
                  Apellido
                </Form.Label>
                <InputGroup size="sm">
                  <InputGroup.Prepend>
                    <InputGroup.Text className="bg-light border-right-0 text-muted">
                      <FontAwesomeIcon icon={faUser} />
                    </InputGroup.Text>
                  </InputGroup.Prepend>
                  <Form.Control
                    name="surname"
                    placeholder="Ej. Pérez"
                    value={filterSettings.surname}
                    onChange={handleChange}
                    disabled={loadingFilter}
                    className="border-left-0 bg-light"
                  />
                </InputGroup>
              </Form.Group>
            </Col>

            <Col lg={3} md={6} className="mb-3">
              <Form.Group className="mb-0">
                <Form.Label className="small font-weight-bold text-secondary mb-1">
                  DNI
                </Form.Label>
                <InputGroup size="sm">
                  <InputGroup.Prepend>
                    <InputGroup.Text className="bg-light border-right-0 text-muted">
                      <FontAwesomeIcon icon={faIdCard} />
                    </InputGroup.Text>
                  </InputGroup.Prepend>
                  <Form.Control
                    name="dni"
                    placeholder="Número de documento"
                    value={filterSettings.dni}
                    onChange={handleChange}
                    disabled={loadingFilter}
                    className="border-left-0 bg-light"
                  />
                </InputGroup>
              </Form.Group>
            </Col>

            <Col lg={3} md={6} className="mb-3">
              <Form.Group className="mb-0">
                <Form.Label className="small font-weight-bold text-secondary mb-1">
                  Teléfono
                </Form.Label>
                <InputGroup size="sm">
                  <InputGroup.Prepend>
                    <InputGroup.Text className="bg-light border-right-0 text-muted">
                      <FontAwesomeIcon icon={faPhone} />
                    </InputGroup.Text>
                  </InputGroup.Prepend>
                  <Form.Control
                    name="phone"
                    placeholder="Sin guiones ni espacios"
                    value={filterSettings.phone}
                    onChange={handleChange}
                    disabled={loadingFilter}
                    className="border-left-0 bg-light"
                  />
                </InputGroup>
              </Form.Group>
            </Col>

            <Col lg={4} md={6} className="mb-3">
              <Form.Group className="mb-0">
                <Form.Label className="small font-weight-bold text-secondary mb-1">
                  Correo Electrónico
                </Form.Label>
                <InputGroup size="sm">
                  <InputGroup.Prepend>
                    <InputGroup.Text className="bg-light border-right-0 text-muted">
                      <FontAwesomeIcon icon={faEnvelope} />
                    </InputGroup.Text>
                  </InputGroup.Prepend>
                  <Form.Control
                    name="email"
                    placeholder="email@ejemplo.com"
                    value={filterSettings.email}
                    onChange={handleChange}
                    disabled={loadingFilter}
                    className="border-left-0 bg-light"
                  />
                </InputGroup>
              </Form.Group>
            </Col>

            <Col lg={3} md={6} className="mb-3">
              <Form.Group className="mb-0">
                <Form.Label className="small font-weight-bold text-secondary mb-1">
                  Comisión
                </Form.Label>
                <InputGroup size="sm">
                  <InputGroup.Prepend>
                    <InputGroup.Text className="bg-light border-right-0 text-muted">
                      <FontAwesomeIcon icon={faChalkboardTeacher} />
                    </InputGroup.Text>
                  </InputGroup.Prepend>
                  <Form.Control
                    as="select"
                    name="classroom"
                    value={filterSettings.classroom}
                    onChange={handleChange}
                    disabled={loadingFilter}
                    className="border-left-0 bg-light"
                  >
                    <option value="">Todas las comisiones</option>
                    {classroomList?.crList?.map((classroomData: any, index: number) => (
                      <option key={index} value={classroomData.classroom}>
                        {classroomData.classroom}
                      </option>
                    ))}
                  </Form.Control>
                </InputGroup>
              </Form.Group>
            </Col>

            <Col lg={3} md={6} className="mb-3">
              <Form.Group className="mb-0">
                <Form.Label className="small font-weight-bold text-secondary mb-1">
                  Estado
                </Form.Label>
                <InputGroup size="sm">
                  <InputGroup.Prepend>
                    <InputGroup.Text className="bg-light border-right-0 text-muted">
                      <FontAwesomeIcon icon={faUserCheck} />
                    </InputGroup.Text>
                  </InputGroup.Prepend>
                  <Form.Control
                    as="select"
                    name="isActive"
                    value={filterSettings.isActive}
                    onChange={handleChange}
                    disabled={loadingFilter}
                    className="border-left-0 bg-light"
                  >
                    <option value="">Todos los estados</option>
                    <option value="up">Activo</option>
                    <option value="posibleDown">Posible baja</option>
                    <option value="down">Baja</option>
                    <option value="done">Finalizó cursado</option>
                    <option value="prepay">Seña</option>
                    <option value="commissionChanged">Cambio de comisión</option>
                  </Form.Control>
                </InputGroup>
              </Form.Group>
            </Col>

            <Col lg={2} md={6} className="mb-3">
              <Form.Group className="mb-0">
                <Form.Label className="small font-weight-bold text-secondary mb-1">
                  Beca
                </Form.Label>
                <InputGroup size="sm">
                  <InputGroup.Prepend>
                    <InputGroup.Text className="bg-light border-right-0 text-muted">
                      <FontAwesomeIcon icon={faGraduationCap} />
                    </InputGroup.Text>
                  </InputGroup.Prepend>
                  <Form.Control
                    as="select"
                    name="scholarship"
                    value={filterSettings.scholarship}
                    onChange={handleChange}
                    disabled={loadingFilter}
                    className="border-left-0 bg-light"
                  >
                    <option value="">Todas</option>
                    <option value="beca">Con Beca</option>
                  </Form.Control>
                </InputGroup>
              </Form.Group>
            </Col>
          </Row>

          <div className="d-flex justify-content-end align-items-center pt-2 border-top mt-2" style={{ gap: "10px" }}>
            <Button
              type="button"
              variant="outline-secondary"
              size="sm"
              onClick={cleanSearch}
              disabled={loadingFilter || !hasActiveFilters}
              className="px-3"
            >
              <FontAwesomeIcon icon={faTimes} className="mr-1" /> Limpiar
            </Button>
            <Button
              type="submit"
              variant="primary"
              size="sm"
              disabled={loadingFilter}
              className="px-4 font-weight-bold shadow-sm"
            >
              <FontAwesomeIcon icon={faSearch} className="mr-1" />
              {loadingFilter ? "Buscando..." : "Buscar Estudiantes"}
            </Button>
          </div>
        </Form>
      </Card.Body>
    </Card>
  );
}
